import { MarkdownParser, type ParseSpec } from 'prosemirror-markdown';
import type Token from 'markdown-it/lib/token.mjs';
import type { JSONContent } from '@tiptap/core';
import { nexusSchema } from '@nexus/editor/schema';
import { parse as parseYaml } from 'yaml';
import { createTokenizer, type WikiTarget } from './tokenizer';

function listIsTight(tokens: Token[], i: number): boolean {
  while (++i < tokens.length) {
    const type = tokens[i]!.type;
    if (type !== 'list_item_open' && type !== 'task_item_open') return tokens[i]!.hidden;
  }
  return false;
}

type Colored = { color: string | null };
const alignOf = (tok: Token) => {
  const align = (tok.meta as { align?: string } | null)?.align;
  return align ? { textAlign: align } : null;
};

const tight = (_tok: Token, tokens: Token[], i: number) => ({ tight: listIsTight(tokens, i) });

const specs: Record<string, ParseSpec> = {
  blockquote: { block: 'blockquote' },
  paragraph: { block: 'paragraph', getAttrs: (tok) => alignOf(tok) },
  list_item: { block: 'listItem' },
  task_item: {
    block: 'taskItem',
    getAttrs: (tok) => {
      const status = (tok.meta as { status: string }).status;
      return { status, checked: status === 'x' || status === 'X' };
    },
  },
  bullet_list: {
    block: 'bulletList',
    getAttrs: (tok, tokens, i) => ({ ...tight(tok, tokens, i), bullet: tok.markup || '-' }),
  },
  task_list: {
    block: 'taskList',
    getAttrs: (tok, tokens, i) => ({ ...tight(tok, tokens, i), bullet: tok.markup || '-' }),
  },
  ordered_list: {
    block: 'orderedList',
    getAttrs: (tok, tokens, i) => ({
      start: Number(tok.attrGet('start') ?? 1),
      ...tight(tok, tokens, i),
    }),
  },
  heading: {
    block: 'heading',
    getAttrs: (tok) => ({ level: Number(tok.tag.slice(1)), ...alignOf(tok) }),
  },
  code_block: { block: 'codeBlock', noCloseToken: true },
  fence: {
    block: 'codeBlock',
    getAttrs: (tok) => ({ language: tok.info.trim() || null }),
    noCloseToken: true,
  },
  hr: { node: 'horizontalRule' },
  image: {
    node: 'image',
    getAttrs: (tok) => ({
      src: tok.attrGet('src'),
      title: tok.attrGet('title') || null,
      alt: tok.children?.map((c) => c.content).join('') || null,
    }),
  },
  hardbreak: { node: 'hardBreak' },
  // Obsidian shows a single newline as a line break, so keep it as one.
  softbreak: { node: 'hardBreak' },
  em: { mark: 'italic' },
  strong: { mark: 'bold' },
  s: { mark: 'strike' },
  mark: { mark: 'highlight' },
  underline: { mark: 'underline' },
  color: { mark: 'textStyle', getAttrs: (tok) => ({ color: (tok.meta as Colored).color }) },
  hcolor: { mark: 'highlight', getAttrs: (tok) => ({ color: (tok.meta as Colored).color }) },
  link: {
    mark: 'link',
    getAttrs: (tok) => ({ href: tok.attrGet('href'), title: tok.attrGet('title') || null }),
  },
  code_inline: { mark: 'code', noCloseToken: true },
  wikilink: {
    node: 'wikiLink',
    getAttrs: (tok) => ({ ...(tok.meta as WikiTarget), pageId: null }),
  },
  embed: {
    node: 'embed',
    getAttrs: (tok) => {
      const { target, heading, alias } = tok.meta as WikiTarget;
      // `![[foto.png|300]]` (or `|300x200`): display width.
      const width = alias && /^\d+(x\d+)?$/.test(alias) ? parseInt(alias, 10) : null;
      return { target, heading, pageId: null, width };
    },
  },
  hashtag: { node: 'hashtag', getAttrs: (tok) => ({ tag: (tok.meta as { tag: string }).tag }) },
  html_inline: { node: 'rawInline', getAttrs: (tok) => ({ text: tok.content }) },
  raw_inline: { node: 'rawInline', getAttrs: (tok) => ({ text: tok.content }) },
  html_block: {
    node: 'rawBlock',
    getAttrs: (tok) => ({ text: tok.content.replace(/\n+$/, '') }),
  },
  raw_block: { node: 'rawBlock', getAttrs: (tok) => ({ text: tok.content }) },
  callout: {
    block: 'callout',
    getAttrs: (tok) => tok.meta as { kind: string; fold: string | null; title: string },
  },
  columns: { block: 'columns' },
  column: { block: 'column' },
  table: { block: 'table' },
  thead: { ignore: true },
  tbody: { ignore: true },
  tr: { block: 'tableRow' },
  th: { block: 'tableHeader' },
  td: { block: 'tableCell' },
};

let parser: MarkdownParser | null = null;

export interface ParsedMarkdown {
  /** Parsed YAML front matter, or null when there is none (or it is invalid). */
  frontmatter: Record<string, unknown> | null;
  /** Front matter text that could not be parsed as YAML, kept for the user to fix. */
  invalidFrontmatter: string | null;
  doc: JSONContent;
}

const FRONTMATTER_RE = /^---[ \t]*\n([\s\S]*?)\n---[ \t]*(?:\n|$)/;

/** Normalise line endings and strip a UTF-8 BOM (both common in the imported vault). */
export function normalizeSource(md: string): string {
  return (md.charCodeAt(0) === 0xfeff ? md.slice(1) : md).replace(/\r\n?/g, '\n');
}

export function splitFrontmatter(md: string): {
  frontmatter: Record<string, unknown> | null;
  invalidFrontmatter: string | null;
  body: string;
} {
  const src = normalizeSource(md);
  const m = FRONTMATTER_RE.exec(src);
  if (!m) return { frontmatter: null, invalidFrontmatter: null, body: src };
  const body = src.slice(m[0].length);
  try {
    const data: unknown = parseYaml(m[1]!);
    if (data === null || data === undefined)
      return { frontmatter: {}, invalidFrontmatter: null, body };
    if (typeof data !== 'object' || Array.isArray(data)) throw new Error('not a mapping');
    return { frontmatter: data as Record<string, unknown>, invalidFrontmatter: null, body };
  } catch {
    return { frontmatter: null, invalidFrontmatter: m[1]!, body };
  }
}

/** Parse Obsidian-flavoured Markdown into a page document (+ front matter). */
export function parseMarkdown(md: string): ParsedMarkdown {
  parser ??= new MarkdownParser(nexusSchema(), createTokenizer(), specs);
  const { frontmatter, invalidFrontmatter, body } = splitFrontmatter(md);
  const doc = parser.parse(body).toJSON() as JSONContent;
  return { frontmatter, invalidFrontmatter, doc };
}
